"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { Locale } from "@/i18n";

// Web Speech API type declarations
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface VoiceRecognitionOptions {
  locale: Locale;
  onResult: (transcript: string) => void;
  onError?: (error: string) => void;
}

interface VoiceRecognitionReturn {
  isListening: boolean;
  isProcessing: boolean; // New: indicates waiting for final result
  isSupported: boolean;
  isPermissionGranted: boolean | null; // null = checking, true = allowed/prompt, false = denied
  transcript: string;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  requestPermission: () => Promise<boolean>;
}

export function useVoiceRecognition({
  locale,
  onResult,
  onError,
}: VoiceRecognitionOptions): VoiceRecognitionReturn {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // New: waiting for final result
  const [isSupported, setIsSupported] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState<boolean | null>(null); // null = checking, true = allowed/prompt, false = denied
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isStoppedManuallyRef = useRef(false);

  // Check browser support and permission on mount
  useEffect(() => {
    // Check if running in browser
    if (typeof window === "undefined") {
      setIsSupported(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    // Check if API exists
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    // Additional check: Safari on macOS doesn't support Web Speech API
    // We can detect this by checking if it's Safari and not on iOS
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMac = /Mac/.test(navigator.userAgent);

    // Desktop Safari doesn't support Web Speech API
    if (isSafari && isMac && !isIOS) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    // Check microphone permission status on mount
    const checkPermission = async () => {
      try {
        // Try to query permission state
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });

          console.log("🎤 Initial permission state:", result.state);

          // Only set true if actually granted, otherwise null (show button but need to request)
          // Hide button only if explicitly denied
          if (result.state === "granted") {
            setIsPermissionGranted(true);
          } else if (result.state === "denied") {
            setIsPermissionGranted(false);
          } else {
            // "prompt" state - show button but will need to request permission on click
            setIsPermissionGranted(null);
          }

          // Listen for permission changes
          result.addEventListener("change", () => {
            console.log("🎤 Permission state changed to:", result.state);
            if (result.state === "granted") {
              setIsPermissionGranted(true);
            } else if (result.state === "denied") {
              setIsPermissionGranted(false);
            } else {
              setIsPermissionGranted(null);
            }
          });
        } else {
          // Permissions API not available - optimistic, show button
          setIsPermissionGranted(null);
        }
      } catch {
        // Permission query failed - show button (will prompt on use)
        console.log("🎤 Permission query not supported, showing button");
        setIsPermissionGranted(null);
      }
    };

    checkPermission();
  }, []);

  // Initialize recognition
  useEffect(() => {
    if (!isSupported) return;

    // Don't auto-initialize - we'll create on demand
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // Ignore cleanup errors
        }
      }
    };
  }, [isSupported]);

  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    // Clean up any existing recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        // Ignore
      }
    }

    // Create a fresh recognition instance each time
    const recognition = new SpeechRecognition();

    // Map locale to speech recognition language codes
    const langMap: Record<Locale, string> = {
      en: "en-US",
      "zh-CN": "zh-CN",
      "zh-TW": "zh-TW",
    };

    recognition.lang = langMap[locale] || "en-US";
    recognition.continuous = true; // Keep listening even with pauses
    recognition.interimResults = false; // Only final results
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      // Only get NEW results starting from resultIndex
      // event.resultIndex tells us where the new results start
      const newResults = Array.from(event.results).slice(event.resultIndex);
      const newTranscript = newResults.map((result) => result[0].transcript).join(" ");

      console.log("🎤 Voice recognition result:", newTranscript);

      // Send only the NEW transcript to input field
      onResult(newTranscript);

      // Clear processing state after result is delivered
      setIsProcessing(false);
    };

    recognition.onstart = () => {
      console.log("🎤 Started listening");
      // If we got here, permission was granted
      // The Permissions API listener will update isPermissionGranted
      setIsListening(true);
    };

    recognition.onerror = (event) => {
      const errorMessage = event.error;
      console.error("🎤 Voice recognition error:", errorMessage);

      // Ignore "aborted" errors - these are expected when stopping manually
      if (errorMessage === "aborted") {
        console.log("🎤 Recognition aborted (expected)");
        return;
      }

      // Handle permission denial - update state
      if (errorMessage === "not-allowed") {
        console.log("🎤 Permission denied by user");
        setIsPermissionGranted(false);
        // The Permissions API listener should also pick this up, but set it here too for immediate feedback
      }

      setError(errorMessage);
      onError?.(errorMessage);
      setIsListening(false);
      setIsProcessing(false); // Clear processing on error
    };

    recognition.onend = () => {
      console.log("🎤 Recognition ended");
      setIsListening(false);
      setIsProcessing(false); // Clear processing when ended
      isStoppedManuallyRef.current = false;
    };

    recognitionRef.current = recognition;

    setError(null);
    setTranscript("");
    isStoppedManuallyRef.current = false;

    try {
      console.log("🎤 Attempting to start recognition...");
      recognition.start();
      // Don't set isListening here - wait for onstart event
    } catch (err) {
      console.error("🎤 Failed to start:", err);
      setError("Failed to start voice recognition");
      onError?.("Failed to start voice recognition");
    }
  }, [locale, onResult, onError]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    isStoppedManuallyRef.current = true;

    // Set to processing state - waiting for final result
    setIsListening(false); // Stop showing "listening" indicator
    setIsProcessing(true); // Show "processing" indicator
    console.log("🎤 Stopping and waiting for final result...");

    try {
      // Use stop() instead of abort() - waits for final result
      recognitionRef.current.stop();
    } catch (err) {
      console.error("🎤 Failed to stop:", err);
      setError("Failed to stop voice recognition");
      onError?.("Failed to stop voice recognition");
      setIsProcessing(false); // Clear processing on error
    }
  }, [onError]);

  // Simple stub - permission is handled by browser when startListening is called
  const requestPermission = useCallback(async (): Promise<boolean> => {
    return isSupported;
  }, [isSupported]);

  return {
    isListening,
    isProcessing,
    isSupported,
    isPermissionGranted,
    transcript,
    error,
    startListening,
    stopListening,
    requestPermission,
  };
}
